/**
 * ModelCard - Individual model selection card with avatar, provider badge, and mini stats.
 */

import { useCallback } from 'react';
import { Check, Monitor, Download, Star } from 'lucide-react';
import { useTranslation } from '@/hooks/useTranslation';
import { PROVIDER_CONFIG } from './providerConfig';
import { AvatarImage } from './AvatarImage';
import { MiniStats } from './MiniStats';
import type { LLMModelOption } from '@/types';

export function ModelCard({
  model,
  label,
  isSelected,
  isFree: _isFree,
  isWebLocked = false,
  onClick,
  disabled,
  onHover,
  onLeave,
  needsDownload = false,
  recommended = false,
}: {
  model: LLMModelOption;
  label: string;
  isSelected: boolean;
  isFree: boolean;
  isWebLocked?: boolean;
  onClick: () => void;
  disabled: boolean;
  onHover: (modelId: string, name: string, rect: DOMRect) => void;
  onLeave: () => void;
  needsDownload?: boolean;
  recommended?: boolean;
}) {
  const { t } = useTranslation();
  const modelId = model.value;
  const provider = modelId.split('/')[0];
  const providerConfig = PROVIDER_CONFIG[provider] || {
    name: provider,
    color: 'text-gray-400',
    bgColor: 'bg-gray-500/10',
    borderColor: 'border-gray-500/20',
  };

  const handleAvatarHover = useCallback(
    (rect: DOMRect) => {
      onHover(modelId, label, rect);
    },
    [modelId, label, onHover]
  );

  return (
    <button
      type="button"
      onClick={onClick}
      disabled={disabled}
      className={`
        relative flex flex-col items-center p-2 rounded-xl border-2 transition-all min-h-[120px] h-full
        ${
          isSelected
            ? 'border-blue-500 bg-blue-500/15 shadow-md ring-2 ring-blue-500/30'
            : recommended
              ? 'border-purple-400/60 bg-purple-500/10 hover:border-purple-400 hover:shadow-sm'
              : `${providerConfig.borderColor} ${providerConfig.bgColor} hover:border-blue-400/50 hover:shadow-sm`
        }
        ${disabled ? 'opacity-50 cursor-not-allowed' : 'cursor-pointer'}
        ${isWebLocked ? 'opacity-60 grayscale-[50%] pointer-events-none' : ''}
      `}
    >
      {/* Web-locked overlay for free models on serverless */}
      {isWebLocked && (
        <div
          className="absolute inset-0 z-10 flex items-center justify-center pointer-events-auto cursor-pointer rounded-xl hover:bg-black/20 transition-colors group/badge"
          onClick={(e) => {
            e.stopPropagation();
            window.dispatchEvent(new CustomEvent('navigate-to-mode', { detail: 'download' }));
          }}
        >
          <div className="bg-emerald-900/80 backdrop-blur-sm rounded-lg px-3 py-2 flex items-center gap-1.5 border border-emerald-500/30 shadow-lg shadow-emerald-500/10 group-hover/badge:border-emerald-400/50 group-hover/badge:shadow-emerald-500/20 transition-all">
            <Monitor className="w-3.5 h-3.5 text-emerald-400" />
            <span className="text-[10px] text-emerald-300 font-semibold">
              {t('llm.freeOnDesktop', 'Free on Desktop')}
            </span>
          </div>
        </div>
      )}

      {/* Selection indicator */}
      {isSelected && (
        <div className="absolute top-1 right-1 w-4 h-4 bg-blue-500 rounded-full flex items-center justify-center">
          <Check className="w-2.5 h-2.5 text-white" />
        </div>
      )}

      {/* Recommended badge (top-left) — shown for the suggested default model.
          Hidden when an Ollama download badge occupies the same corner. */}
      {recommended && !needsDownload && (
        <div
          className="absolute top-1 left-1 w-4 h-4 bg-purple-500 rounded-full flex items-center justify-center"
          title={t('providerPicker.recommended', 'Recommended')}
          aria-label={t('providerPicker.recommended', 'Recommended')}
        >
          <Star className="w-2.5 h-2.5 text-white fill-white" />
        </div>
      )}

      {/* Download indicator for Ollama models not installed */}
      {needsDownload && (
        <div className="absolute top-1 left-1 px-1 py-0.5 bg-orange-500 text-white text-[8px] font-bold rounded-full flex items-center gap-0.5">
          <Download className="w-2 h-2" />
        </div>
      )}

      {/* Avatar with hover */}
      <AvatarImage
        modelId={modelId}
        size="md"
        className="mb-1"
        onHover={handleAvatarHover}
        onLeave={onLeave}
      />

      {/* Model name - reserve 2 lines so 1-line and 2-line names keep cards equal height */}
      <span className="min-h-[2.5em] text-[11px] font-semibold text-center line-clamp-2 leading-tight text-white">
        {label}
      </span>

      {/* Provider badge */}
      <span className={`text-[8px] ${providerConfig.color}`}>{providerConfig.name}</span>

      {/* Mini Stats */}
      <MiniStats model={model} />
    </button>
  );
}

export default ModelCard;
